import { useCallback, useEffect, useState } from 'react';
import { Box, MediaObject } from '@zillow/constellation-10';
import { useQueryClient } from '@zg-rentals/react-query5';
import {
  ChatAvatar,
  type ChatBubbleVariant,
  getScreenReaderSenderName,
  isMessageStatusVariant,
  LowPrioritySystemMessage,
  MESSAGE_STATUS,
  MessageFooter,
  type MessageStatusVariant,
} from '@zg-rentals/satellite-components';
import type { SatelliteWebLandlordConversationAddMessageRequestDto } from '@zg-rentals/ts-satellite-client';
import { useSatelliteWebLandlordConversationHandler_addMessage } from '@zg-rentals/ts-satellite-client-react-query';
import { getConversationMessagesQueryKey } from '../../../../hooks/useConversationMessagesQuery';
import {
  buildResendCacheUpdate,
  type ConversationMessagesData,
  MessageSendFailureReason,
  toConversationMessageAttachments,
} from '../../../../utils/conversationMessageUtils';
import StringConstants from '../../../../constants/StringConstants';
import { isMessageTooLongError } from '../../../../utils/errorUtils';
import stringUtils from '../../../../utils/stringUtils';
import type { AttachmentsForMessage } from '../../../MessageAttachments';
import MessageAttachments from '../../../MessageAttachments';
import type {
  ConversationIdentity,
  HandleOpenDeleteAttachmentsModal,
  HandleOpenImagesViewer,
  OnResendMessageComplete,
} from '../../../MessageListItems';
import type { TriggerLocation } from '../../../../constants/clickstreamEvents/globals';
import ChatBubbleWithActions from '../../../ChatBubbleWithActions';
import { useUserWarnings } from '../../../../hooks/useUserWarnings';

type Props = {
  attachments: AttachmentsForMessage;
  conversation: ConversationIdentity;
  leadId?: string | null;
  onResendMessageComplete?: OnResendMessageComplete;
  resendBlockedText?: string;
  isMessageOwner: boolean;
  messageContent: string;
  messageDateMs: string;
  senderName: string;
  sendFailureReason?: MessageSendFailureReason;
  shouldDisplaySentStatus: boolean;
  uiTreatmentType: string;
  messageId: string;
  openDeleteAttachmentsModal: HandleOpenDeleteAttachmentsModal;
  openImagesViewer: HandleOpenImagesViewer;
  triggerLocation: TriggerLocation;
};

const getAttachmentZDocIds = (attachments: AttachmentsForMessage): Array<string> => {
  return attachments.map(({ zDocId }) => zDocId || '');
};

const ListingMessage = ({
  attachments,
  conversation,
  leadId,
  onResendMessageComplete,
  resendBlockedText,
  isMessageOwner,
  messageContent,
  messageDateMs,
  senderName,
  sendFailureReason,
  shouldDisplaySentStatus,
  messageId,
  uiTreatmentType,
  openDeleteAttachmentsModal,
  openImagesViewer,
  triggerLocation,
}: Props) => {
  const queryClient = useQueryClient();
  const { attachmentSafetySystemMessageData } = useUserWarnings();
  const { conversationId, listingAlias = '', clientTimeZone } = conversation;

  const { mutateAsync: addMessageMutation } = useSatelliteWebLandlordConversationHandler_addMessage({
    meta: {
      context: {
        api: 'addMessage',
        details: 'ListingMessage - Error resending failed message item.',
        conversationId,
        listingAlias,
      },
    },
  });
  const statusFromUiTreatment = uiTreatmentType.split('-').length > 1 ? uiTreatmentType.split('-')[1] : undefined;
  const validStatus = isMessageStatusVariant(statusFromUiTreatment) ? statusFromUiTreatment : MESSAGE_STATUS.SENT;
  const [sendStatus, setSendStatus] = useState<MessageStatusVariant | undefined>(validStatus);

  // Sync from props when cache updates (e.g. send fails and AddMessage writes 'lowPriority-failed').
  useEffect(() => {
    setSendStatus(validStatus);
  }, [validStatus]);

  // Retrying an over-limit message always fails, and a conversation that can't send hands down its
  // own reason; either way the footer explains instead of offering "Try again".
  const isTooLongFailure = sendFailureReason === MessageSendFailureReason.MessageTooLong;
  const retryBlockedText = isTooLongFailure ? StringConstants.FAILED_SEND_LONG_MESSAGE_TEXT : resendBlockedText;

  const hasAttachments = attachments && !!attachments.length && Object.keys(attachments[0]).length !== 0;
  const allAttachmentsExpired = hasAttachments && attachments.every((a) => a.isAttachmentExpired);
  const allAttachmentsDeleted = hasAttachments && attachments.every((a) => a.isAttachmentDeleted);
  const isMessageContentEmpty = stringUtils.isEmptyString(messageContent);
  const chatBubbleVariant: ChatBubbleVariant = isMessageOwner ? 'messageOwner' : 'default';
  const shouldAdjustOpacity = sendStatus === MESSAGE_STATUS.SENDING || sendStatus === MESSAGE_STATUS.FAILED;

  const handleResendMessage = useCallback(async () => {
    setSendStatus(MESSAGE_STATUS.SENDING);
    const zDocIds = hasAttachments ? getAttachmentZDocIds(attachments) : [];
    const postData: SatelliteWebLandlordConversationAddMessageRequestDto = {
      conversationId,
      message: messageContent,
      uploadedZdocIds: zDocIds,
    };

    try {
      const newMessage = await addMessageMutation({ postData });
      const queryKey = getConversationMessagesQueryKey({ conversationId, clientTimeZone });

      // The Proxit response omits attachments, so pass the ones we resent so the inserted
      // message keeps them until the next getMessages poll.
      const resendAttachments = hasAttachments ? toConversationMessageAttachments(attachments) : [];

      // Reducer extracted into `buildResendCacheUpdate` (see its docstring) so the
      // resend cache lifecycle is unit-testable without rendering this component.
      queryClient.setQueryData<ConversationMessagesData>(queryKey, (existing) =>
        existing
          ? buildResendCacheUpdate(existing, newMessage, messageId, { attachments: resendAttachments })
          : existing,
      );

      onResendMessageComplete?.({ conversation });
    } catch (error) {
      // Error logging happens via the mutation's `meta.context`. A too-long rejection is tagged on
      // the cached message so the no-retry verdict flows through `sendFailureReason` and survives remounts.
      if (isMessageTooLongError(error, messageContent)) {
        const queryKey = getConversationMessagesQueryKey({ conversationId, clientTimeZone });
        queryClient.setQueryData<ConversationMessagesData>(queryKey, (existing) =>
          existing
            ? {
                ...existing,
                conversation: existing.conversation.map((m) =>
                  m.messageId === messageId ? { ...m, sendFailureReason: MessageSendFailureReason.MessageTooLong } : m,
                ),
              }
            : existing,
        );
      }
      setSendStatus(MESSAGE_STATUS.FAILED);
    }
  }, [
    conversationId,
    clientTimeZone,
    messageContent,
    onResendMessageComplete,
    queryClient,
    addMessageMutation,
    attachments,
    hasAttachments,
    messageId,
    conversation,
  ]);

  return (
    <>
      <MediaObject
        media={!isMessageOwner && <ChatAvatar senderFullName={senderName} type="user" />}
        css={{
          alignItems: 'end',
          display: 'flex',
          flexDirection: isMessageOwner ? 'row-reverse' : 'row',
          gap: 0,
          whiteSpace: 'pre-wrap',
          width: '100%',
        }}
      >
        <Box css={{ opacity: shouldAdjustOpacity ? 0.4 : 1 }}>
          {!isMessageContentEmpty && (
            <ChatBubbleWithActions
              senderName={senderName}
              variant={chatBubbleVariant}
              messageContent={messageContent}
              isMessageOwner={isMessageOwner}
              listingAlias={listingAlias}
              leadId={leadId}
              triggerLocation={triggerLocation}
            />
          )}

          {hasAttachments && (
            <MessageAttachments
              attachments={attachments}
              conversationId={conversationId}
              listingAlias={listingAlias}
              leadId={leadId}
              isMessageOwner={isMessageOwner}
              isMessageSent={sendStatus === MESSAGE_STATUS.SENT}
              messageId={messageId}
              openDeleteAttachmentsModal={openDeleteAttachmentsModal}
              openImagesViewer={openImagesViewer}
              triggerLocation={triggerLocation}
            />
          )}
        </Box>

        <MessageFooter
          failureText={retryBlockedText}
          isMessageOwner={isMessageOwner}
          isSenderAnnouncedByBubble={
            !isMessageContentEmpty && !!getScreenReaderSenderName(chatBubbleVariant, senderName)
          }
          messageDateMs={messageDateMs}
          resendMessage={retryBlockedText ? undefined : handleResendMessage}
          senderName={senderName}
          sendStatus={sendStatus}
          shouldDisplaySentStatus={shouldDisplaySentStatus}
        />
      </MediaObject>

      {hasAttachments &&
        !isMessageOwner &&
        !allAttachmentsExpired &&
        !allAttachmentsDeleted &&
        attachmentSafetySystemMessageData && (
          <LowPrioritySystemMessage
            message={attachmentSafetySystemMessageData.disclaimerText}
            ctaText={attachmentSafetySystemMessageData.linkText}
            url={attachmentSafetySystemMessageData.linkUrl}
          />
        )}
    </>
  );
};

export default ListingMessage;
